/**
 * Admin AI Usage Page
 *
 * What users generated, and what it cost us:
 * - Aggregate usage and provider cost, this month and since metering started
 * - Anything that makes the cost figure unreliable, stated beside it
 * - Margin: what the month's subscriptions bring in against its whole cost
 * - Tier breakdown
 * - Top users, ranked by generations or by cost, and the accounts below cost
 */

import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  ArrowsClockwise,
  Cloud,
  Cpu,
  CurrencyDollar,
  Receipt,
  Scales,
  TrendDown,
  TrendUp as TrendingUp,
  Users,
  Warning,
} from "@phosphor-icons/react";
import {
  Alert,
  EmptyState,
  HeaderAction,
  PageHeader,
  type PillTone,
  SegmentedControl,
  Spinner,
  StatTile,
  type StatTone,
  StatusPill,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from "@charisol-spark/ui";
import { hasRole } from "@/components/auth/AdminGuard";
import { useAdminAIUsage, useAdminAIUsageDaily } from "@/hooks/useAdmin";
import {
  type AIUsageDailyWindow,
  type AIUsageTopUser,
  COST_NOT_REPORTED,
  type CostSummary,
  type FullyLoadedCost,
  type PlatformCost,
  type PriceChangeReport,
} from "@/services/admin";
import { getErrorMessage } from "@/services/api";
import { cn } from "@/utils/cn";
import { formatMicroUsd, formatPeriod } from "@/utils/formatting";

/** A `YYYY-MM-DD` day, as a short date in UTC. */
function formatDay(day: string): string {
  const parsed = new Date(`${day}T00:00:00Z`);
  return Number.isNaN(parsed.getTime())
    ? day
    : new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        timeZone: "UTC",
      }).format(parsed);
}

const TIER_TONES: Record<string, PillTone> = {
  free: "neutral",
  pro: "violet",
  team: "blue",
  enterprise: "amber",
};

const TASK_TYPE_LABEL = {
  research: "Research",
  auto_draft: "Auto draft",
  metrics_digest: "Metrics digest",
} as const;

function TierPill({ tier }: { tier: string }) {
  return (
    <StatusPill tone={TIER_TONES[tier] ?? "neutral"} className="capitalize">
      {tier}
    </StatusPill>
  );
}

function StatCard({
  label,
  value,
  detail,
  icon,
  tone,
}: {
  label: string;
  value: number | string;
  detail?: string;
  icon: React.ComponentProps<typeof StatTile>["icon"];
  tone?: StatTone;
}) {
  return (
    <StatTile
      label={label}
      value={typeof value === "number" ? value.toLocaleString() : value}
      hint={detail}
      icon={icon}
      tone={tone}
    />
  );
}

/**
 * Says when the cost total is wrong, and in which direction, instead of
 * showing a figure that looks exact.
 */
function CostReliabilityNotice({ cost }: { cost: CostSummary }) {
  const problems: string[] = [];
  if (cost.unpricedCalls > 0) {
    problems.push(
      `${cost.unpricedCalls.toLocaleString()} ${cost.unpricedCalls === 1 ? "call has" : "calls have"} no published price and ${cost.unpricedCalls === 1 ? "was" : "were"} recorded at $0, so the total is understated.`
    );
  }
  if (cost.fallbackCalls > 0) {
    problems.push(
      `${cost.fallbackCalls.toLocaleString()} ${cost.fallbackCalls === 1 ? "call" : "calls"} used a model with no listed rate and ${cost.fallbackCalls === 1 ? "was" : "were"} priced at the most expensive one, so the total may be overstated.`
    );
  }
  if (problems.length === 0) return null;

  return (
    <div
      role="status"
      className="flex gap-3 rounded-xl border border-amber-300/60 bg-amber-50 p-4 text-sm text-amber-900 dark:border-amber-600/30 dark:bg-amber-900/10 dark:text-amber-200"
    >
      <Warning className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
      <div className="space-y-1">
        <p className="font-medium">This month&apos;s cost is not exact.</p>
        {problems.map((problem) => (
          <p key={problem}>{problem}</p>
        ))}
        <p className="text-xs opacity-80">
          Add the missing rates to the billing config to fix this.
        </p>
      </div>
    </div>
  );
}

/** Warn this far ahead, matching the daily cost job. */
const PRICE_CHANGE_WARNING_DAYS = 90;

function formatPercent(ratio: number | null): string {
  if (ratio === null) return "n/a";
  const sign = ratio >= 0 ? "+" : "";
  return `${sign}${(ratio * 100).toFixed(0)}%`;
}

/** A margin in the danger colour when it is a loss. */
function MarginFigure({
  microUsd,
  className,
}: {
  /** Null when the price is unknown; undefined from an API without margin. */
  microUsd: number | null | undefined;
  className?: string;
}) {
  if (microUsd === null || microUsd === undefined) {
    return (
      <span
        className={cn("text-muted-foreground", className)}
        title="No known price"
      >
        —
      </span>
    );
  }
  return (
    <span className={cn(microUsd < 0 && "text-destructive", className)}>
      {formatMicroUsd(microUsd)}
    </span>
  );
}

/** Where a user's price came from, for the tables. */
function priceLabel(user: AIUsageTopUser): string {
  if (user.priceMicroUsd === null) return "No list price";
  if (user.priceMicroUsd === undefined) return "";
  return user.priceSource === "override"
    ? `${formatMicroUsd(user.priceMicroUsd)} custom`
    : formatMicroUsd(user.priceMicroUsd);
}

/**
 * What the month's subscriptions bring in against what the month cost.
 *
 * Revenue is every user's price, list or custom, whether or not they were
 * active: they are charged regardless. Cost is the whole period — every
 * provider call, attributed or not, plus the platform bill — so the margin
 * here is smaller than the sum of the per-user ones.
 */
function MarginCard({
  month,
  revenueMicroUsd,
  costMicroUsd,
  marginMicroUsd,
  marginRatio,
  unpricedUsers,
  platformRecorded,
}: {
  month: string;
  revenueMicroUsd: number;
  costMicroUsd: number;
  marginMicroUsd: number | null;
  marginRatio: number | null;
  unpricedUsers: number;
  platformRecorded: boolean;
}) {
  const loss = marginMicroUsd !== null && marginMicroUsd < 0;
  return (
    <section
      aria-label="Margin"
      className={cn(
        "space-y-3 rounded-xl border p-5",
        loss
          ? "border-destructive/40 bg-destructive/5"
          : "border-border/50 bg-card"
      )}
    >
      <div className="flex items-center gap-2">
        <Scales className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Margin ({month})
        </h2>
      </div>
      <dl className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <div>
          <dt className="text-xs text-muted-foreground">Revenue</dt>
          <dd className="text-xl font-bold tabular-nums">
            {formatMicroUsd(revenueMicroUsd)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Cost</dt>
          <dd className="text-xl font-bold tabular-nums">
            {formatMicroUsd(costMicroUsd)}
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Margin</dt>
          <dd className="text-xl font-bold tabular-nums">
            <MarginFigure microUsd={marginMicroUsd} />
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Margin ratio</dt>
          <dd className="text-xl font-bold tabular-nums">
            {marginRatio === null
              ? "n/a"
              : `${(marginRatio * 100).toFixed(0)}%`}
          </dd>
        </div>
      </dl>
      <p className="text-xs text-muted-foreground">
        Revenue is each user&apos;s monthly price — the plan&apos;s list price,
        or the custom price set on their account — counted whether or not they
        did anything. Cost is provider calls plus AWS
        {platformRecorded ? "" : " (not recorded yet, so counted as $0)"}.
        Nothing is collected: there is no payment provider, so this is what
        accounts are charged, not what was paid.
        {unpricedUsers > 0 &&
          ` ${unpricedUsers.toLocaleString()} ${unpricedUsers === 1 ? "user has" : "users have"} no known price (Enterprise with no custom price set) and ${unpricedUsers === 1 ? "is" : "are"} not in the revenue figure.`}
      </p>
    </section>
  );
}

/** The accounts that cost more than they pay, biggest loss first. */
function BelowCostList({ users }: { users: AIUsageTopUser[] }) {
  return (
    <div className="rounded-xl border border-border/50 bg-card p-5">
      <div className="mb-3 flex items-center gap-2">
        <TrendDown
          className="h-4 w-4 text-muted-foreground"
          aria-hidden="true"
        />
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Below cost
        </h2>
      </div>
      {users.length === 0 ? (
        <p className="py-2 text-sm text-muted-foreground">
          No account has cost more than its price this month.
        </p>
      ) : (
        <ul className="divide-y divide-border/30 text-sm">
          {users.map((user) => (
            <li
              key={user.userId}
              className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2.5"
            >
              <div className="flex min-w-0 items-center gap-3">
                <UserCell user={user} />
                <TierPill tier={user.tier} />
              </div>
              <div className="text-right tabular-nums">
                <MarginFigure
                  microUsd={user.marginMicroUsd}
                  className="font-medium"
                />
                <p className="text-xs text-muted-foreground">
                  {priceLabel(user)} against{" "}
                  {formatMicroUsd(user.fullyLoadedMicroUsd)} fully loaded
                </p>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="mt-3 text-xs text-muted-foreground">
        Fully-loaded cost against the account&apos;s monthly price. Accounts
        with no known price are not listed: their margin is unknown, not
        negative.
      </p>
    </div>
  );
}

function formatRate(rate: {
  inputUsdPerMillion: number;
  outputUsdPerMillion: number;
}): string {
  return `$${rate.inputUsdPerMillion} in / $${rate.outputUsdPerMillion} out per M tokens`;
}

/**
 * Published provider price changes still ahead, at this month's volume.
 *
 * A 2x step on the migration target was previously discoverable only by
 * reading a comment in the rate card. Shown whenever a change is pending,
 * and coloured as a warning inside the window the daily job also warns in.
 */
function PriceChangeNotice({ report }: { report: PriceChangeReport }) {
  if (report.changes.length === 0) return null;
  const soon = report.changes.some(
    (c) => c.daysAway <= PRICE_CHANGE_WARNING_DAYS
  );

  return (
    <section
      aria-label="Published price changes"
      className={cn(
        "space-y-3 rounded-xl border p-5",
        soon
          ? "border-amber-300/60 bg-amber-50 dark:border-amber-600/30 dark:bg-amber-900/10"
          : "border-border/50 bg-card"
      )}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Published price changes
        </h2>
        <p className="text-xs tabular-nums text-muted-foreground">
          At {formatPeriod(report.period)}&apos;s volume:{" "}
          {formatMicroUsd(report.totalMicroUsd)} →{" "}
          {formatMicroUsd(report.projectedMicroUsd)} (
          {formatPercent(report.deltaRatio)})
        </p>
      </div>
      <ul className="space-y-2 text-sm">
        {report.changes.map((change) => (
          <li
            key={`${change.model}-${change.from}`}
            className="flex flex-col gap-0.5"
          >
            <span>
              <span className="font-medium">{change.model}</span> on{" "}
              {formatDay(change.from)}
              <span className="text-muted-foreground">
                {" "}
                · {change.daysAway} days away
              </span>
            </span>
            <span className="text-xs text-muted-foreground">
              {formatRate(change.current)} → {formatRate(change.next)}.{" "}
              {change.currentMicroUsd > 0
                ? `This month's ${formatMicroUsd(change.currentMicroUsd)} on it would be ${formatMicroUsd(change.projectedMicroUsd)} (${formatPercent(change.deltaRatio)}).`
                : "No volume on this model this month."}
            </span>
          </li>
        ))}
      </ul>
      {report.unpricedModels.length > 0 && (
        <p className="text-xs text-muted-foreground">
          Volume on {report.unpricedModels.join(", ")} matches nothing in the
          rate card and is not projected.
        </p>
      )}
    </section>
  );
}

const RANKINGS = [
  { key: "generations", label: "By generations" },
  { key: "cost", label: "By cost" },
] as const;

type Ranking = (typeof RANKINGS)[number]["key"];

function UserCell({ user }: { user: AIUsageTopUser }) {
  // Analysts receive neither name nor email.
  const primary = user.name || user.email;
  const content = primary ? (
    <>
      <p className="font-medium">{primary}</p>
      {user.name && user.email && (
        <p className="text-xs text-muted-foreground">{user.email}</p>
      )}
    </>
  ) : (
    <p className="font-mono text-xs text-muted-foreground">{user.userId}</p>
  );

  if (!hasRole("platform_admin", "platform_support")) return content;
  return (
    <Link
      to={`/users/${user.userId}`}
      className="block hover:text-primary hover:underline"
    >
      {content}
    </Link>
  );
}

function TopUsersTable({ users }: { users: AIUsageTopUser[] }) {
  if (users.length === 0) {
    return (
      <EmptyState
        icon={Users}
        title="No usage recorded this month yet."
        className="py-10"
      />
    );
  }

  return (
    <Table>
      <THead>
        <tr>
          <TH>#</TH>
          <TH>User</TH>
          <TH>Tier</TH>
          <TH className="text-right">Generations</TH>
          <TH className="text-right">Credits</TH>
          <TH className="text-right">AI cost</TH>
          <TH className="text-right">X cost</TH>
          <TH className="text-right">Total cost</TH>
          <TH className="text-right">Fully loaded</TH>
          <TH className="text-right">Margin</TH>
        </tr>
      </THead>
      <TBody>
        {users.map((user, i) => (
          <TR key={user.userId}>
            <TD className="tabular-nums text-muted-foreground">{i + 1}</TD>
            <TD>
              <UserCell user={user} />
            </TD>
            <TD>
              <TierPill tier={user.tier} />
            </TD>
            <TD className="text-right tabular-nums">
              {user.generationsThisMonth.toLocaleString()}
            </TD>
            <TD className="text-right tabular-nums text-muted-foreground">
              {user.creditsThisMonth !== undefined
                ? user.creditsThisMonth.toLocaleString()
                : "—"}
            </TD>
            <TD className="text-right tabular-nums text-muted-foreground">
              {formatMicroUsd(user.aiCostMicroUsd)}
            </TD>
            <TD className="text-right tabular-nums text-muted-foreground">
              {formatMicroUsd(user.xCostMicroUsd)}
            </TD>
            <TD className="text-right font-medium tabular-nums">
              {formatMicroUsd(user.costThisMonthMicroUsd)}
            </TD>
            <TD className="text-right tabular-nums text-muted-foreground">
              {formatMicroUsd(user.fullyLoadedMicroUsd)}
            </TD>
            <TD className="text-right tabular-nums" title={priceLabel(user)}>
              <MarginFigure microUsd={user.marginMicroUsd} />
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

const PAGE_TITLE = "AI Usage & Cost";

const DAILY_WINDOWS: AIUsageDailyWindow[] = [7, 30, 90];

const DAILY_METRICS = [
  { key: "generations", label: "Generations" },
  { key: "credits", label: "Credits" },
  { key: "costMicroUsd", label: "Cost" },
] as const;
type DailyMetricKey = (typeof DAILY_METRICS)[number]["key"];

function formatDailyValue(value: number, metric: DailyMetricKey): string {
  return metric === "costMicroUsd"
    ? formatMicroUsd(value)
    : value.toLocaleString();
}

/**
 * Daily generations/credits/cost, with a 7/30/90-day selector.
 *
 * Days before the series' `since` were never instrumented — they carry no
 * data, not a real zero — so they render as muted bars with no numeric
 * tooltip, and the table below spells out "No data" rather than "0" for the
 * same days. Treating them the same as a zero-activity day would read as
 * "nothing happened" when the truth is "nobody was counting".
 */
function DailyUsageChart() {
  const [days, setDays] = useState<AIUsageDailyWindow>(7);
  const [metric, setMetric] = useState<DailyMetricKey>("generations");
  const { data, isLoading, error } = useAdminAIUsageDaily(days);

  return (
    <div className="rounded-xl border border-border/50 bg-card p-4 sm:p-6">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          Daily Usage
        </h2>
        <div className="flex flex-wrap gap-2">
          <SegmentedControl
            label="Daily metric"
            value={metric}
            onChange={setMetric}
            options={DAILY_METRICS.map((m) => ({
              value: m.key,
              label: m.label,
            }))}
          />
          <SegmentedControl
            label="Daily window"
            value={String(days)}
            onChange={(v) => setDays(Number(v) as AIUsageDailyWindow)}
            options={DAILY_WINDOWS.map((w) => ({
              value: String(w),
              label: `${w}d`,
            }))}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-56 items-center justify-center">
          <Spinner size="md" />
        </div>
      ) : error || !data ? (
        <Alert variant="error">
          {getErrorMessage(error, "Failed to load the daily usage chart.")}
        </Alert>
      ) : (
        <>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              data={data.series}
              margin={{ top: 4, right: 4, left: -20, bottom: 0 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="hsl(var(--border))"
                opacity={0.5}
              />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
                tickFormatter={(v: string) => formatDay(v)}
              />
              <YAxis
                tick={{ fontSize: 11 }}
                stroke="hsl(var(--muted-foreground))"
                allowDecimals={false}
                width={metric === "costMicroUsd" ? 72 : 50}
                tickFormatter={(v: number) => formatDailyValue(v, metric)}
              />
              <Tooltip
                labelFormatter={(label) => formatDay(String(label))}
                formatter={(value, _name, props) => {
                  const point = props.payload as (typeof data.series)[number];
                  const label =
                    DAILY_METRICS.find((m) => m.key === metric)?.label ??
                    metric;
                  if (point.date < data.since) return ["No data yet", label];
                  return [formatDailyValue(Number(value), metric), label];
                }}
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "8px",
                  fontSize: "12px",
                }}
              />
              <Bar dataKey={metric} radius={[3, 3, 0, 0]}>
                {data.series.map((point) => (
                  <Cell
                    key={point.date}
                    fill={
                      point.date < data.since
                        ? "hsl(var(--muted-foreground) / 0.25)"
                        : "hsl(var(--primary))"
                    }
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>

          <p className="mt-2 text-xs text-muted-foreground">
            Muted bars are before {formatDay(data.since)}, when daily tracking
            began. Those days have no data recorded, not zero activity.
          </p>

          {/* Accessible fallback with the same numbers, always rendered —
              not just for screen readers: it is also how "no data" and "zero"
              stay distinguishable once bar height alone can't show it. */}
          <div className="mt-4 overflow-hidden rounded-xl border border-border/50">
            <Table className="text-xs">
              <caption className="sr-only">
                Daily generations, credits and cost for the last {data.days}{" "}
                days. Days before {data.since} have no data recorded.
              </caption>
              <THead>
                <tr>
                  <TH className="py-2">Date</TH>
                  <TH className="py-2 text-right">Generations</TH>
                  <TH className="py-2 text-right">Credits</TH>
                  <TH className="py-2 text-right">Cost</TH>
                </tr>
              </THead>
              <TBody>
                {data.series.map((point) => {
                  const noData = point.date < data.since;
                  return (
                    <TR key={point.date}>
                      <TD className="py-2 tabular-nums">
                        {formatDay(point.date)}
                      </TD>
                      <TD className="py-2 text-right tabular-nums">
                        {noData
                          ? "No data"
                          : point.generations.toLocaleString()}
                      </TD>
                      <TD className="py-2 text-right tabular-nums">
                        {noData ? "No data" : point.credits.toLocaleString()}
                      </TD>
                      <TD className="py-2 text-right tabular-nums">
                        {noData
                          ? "No data"
                          : formatMicroUsd(point.costMicroUsd)}
                      </TD>
                    </TR>
                  );
                })}
              </TBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}

export function AdminAIUsagePage() {
  const { data, isLoading, isFetching, error, refetch } = useAdminAIUsage();
  const [ranking, setRanking] = useState<Ranking>("generations");

  if (isLoading) {
    return (
      <div className="space-y-6">
        <PageHeader title={PAGE_TITLE} />
        <div className="flex h-60 items-center justify-center">
          <Spinner size="md" />
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="space-y-6">
        <PageHeader title={PAGE_TITLE} />
        <Alert variant="error">Failed to load AI usage data.</Alert>
      </div>
    );
  }

  // See COST_NOT_REPORTED. An API from before cost tracking also reported
  // every generation figure as zero, so there is nothing worth showing.
  if (!data.costThisMonth) {
    return (
      <div className="space-y-6">
        <PageHeader title={PAGE_TITLE} />
        <div className="rounded-xl border border-border/50 bg-card">
          <EmptyState icon={Cpu} title={COST_NOT_REPORTED} />
        </div>
      </div>
    );
  }

  const month = formatPeriod(data.period);
  const since = formatPeriod(data.meteringStarted);
  const cost = data.costThisMonth;
  // Absent from an API older than platform cost, as well as when the sync has
  // not run; both mean the same thing to a reader.
  const platform = data.platformThisMonth as PlatformCost | null | undefined;
  const fullyLoaded: FullyLoadedCost = data.fullyLoaded ?? {
    activeUsers: 0,
    platformMicroUsd: 0,
    platformPerActiveUserMicroUsd: 0,
    perActiveUserMicroUsd: 0,
    method: "even-split-across-active-users",
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title={PAGE_TITLE}
        description={`What users generated in ${month} and what it cost us. Cost covers Gemini and X only; AWS hosting is not included yet.`}
        action={
          <HeaderAction
            icon={<ArrowsClockwise className="h-4 w-4" />}
            onClick={() => void refetch()}
            loading={isFetching}
          >
            Refresh
          </HeaderAction>
        }
      />

      {/* Stats grid */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 sm:gap-4 lg:grid-cols-3">
        <StatCard
          tone="violet"
          icon={TrendingUp}
          label={`Generations in ${month}`}
          value={data.totalGenerationsThisMonth}
          detail={
            data.totalCreditsThisMonth !== undefined
              ? `${data.totalCreditsThisMonth.toLocaleString()} credits · ${data.totalUsers.toLocaleString()} users` +
                (data.totalChatMessagesThisMonth
                  ? ` · ${data.totalChatMessagesThisMonth.toLocaleString()} chat messages`
                  : "")
              : `${data.totalUsers.toLocaleString()} users`
          }
        />
        <StatCard
          tone="cyan"
          icon={Cpu}
          label={`Generations since ${since}`}
          value={data.totalGenerations}
          detail={
            data.totalCredits !== undefined
              ? `${data.totalCredits.toLocaleString()} credits`
              : undefined
          }
        />
        <StatCard
          tone="emerald"
          icon={CurrencyDollar}
          label={`Cost in ${month}`}
          value={formatMicroUsd(cost.totalMicroUsd)}
          detail={`AI ${formatMicroUsd(cost.aiMicroUsd)} · X ${formatMicroUsd(cost.xMicroUsd)}`}
        />
        <StatCard
          tone="amber"
          icon={Receipt}
          label={`Cost since ${since}`}
          value={formatMicroUsd(data.costAllTimeMicroUsd)}
        />
        <StatCard
          tone="blue"
          icon={Cloud}
          label={`AWS in ${month}`}
          value={
            platform ? formatMicroUsd(platform.totalMicroUsd) : "Not recorded"
          }
          detail={
            platform
              ? `Through ${formatDay(platform.through)} · tagged resources only`
              : "The daily sync has not recorded it yet"
          }
        />
        <StatCard
          tone="rose"
          icon={Users}
          label="Fully loaded, per active user"
          value={formatMicroUsd(fullyLoaded.perActiveUserMicroUsd)}
          detail={`${fullyLoaded.activeUsers.toLocaleString()} active · ${formatMicroUsd(fullyLoaded.platformPerActiveUserMicroUsd)} of it AWS`}
        />
      </div>

      <p className="text-xs text-muted-foreground">
        AWS cost covers resources carrying this app&apos;s tags, so it is a
        floor: Lambda&apos;s own log groups are not tagged. The per-user share
        is an allocation, not a measurement — AWS bills the environment, and
        nothing in that bill belongs to one user. Cloudflare, which serves the
        frontend, is not included.
      </p>

      <CostReliabilityNotice cost={cost} />

      {data.tasksThisMonth && (
        <section aria-label="Scheduled task runs by type">
          <h2 className="text-sm font-semibold">Scheduled tasks in {month}</h2>
          <p className="mt-1 text-xs text-muted-foreground">
            What runs started by a task schedule or Run now cost, by task type.
            Calls, not runs: a Deep research run makes several.
          </p>
          <dl className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-3">
            {data.tasksThisMonth.map((row) => (
              <div
                key={row.type}
                className="rounded-xl border border-border/50 bg-card p-4"
              >
                <dt className="text-xs text-muted-foreground">
                  {TASK_TYPE_LABEL[row.type]}
                </dt>
                <dd className="text-lg font-bold tabular-nums">
                  {formatMicroUsd(row.microUsd)}
                </dd>
                <dd className="text-xs tabular-nums text-muted-foreground">
                  {row.calls.toLocaleString()} call
                  {row.calls === 1 ? "" : "s"}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <DailyUsageChart />

      {/* Absent from an API older than margin reporting. */}
      {data.revenueMicroUsd !== undefined &&
      data.marginMicroUsd !== undefined ? (
        <MarginCard
          month={month}
          revenueMicroUsd={data.revenueMicroUsd}
          costMicroUsd={cost.totalMicroUsd + fullyLoaded.platformMicroUsd}
          marginMicroUsd={data.marginMicroUsd}
          marginRatio={data.marginRatio ?? null}
          unpricedUsers={data.unpricedUsers ?? 0}
          platformRecorded={Boolean(platform)}
        />
      ) : (
        <p className="text-xs text-muted-foreground">
          This environment&apos;s API does not report margin yet. It will once
          the backend is deployed.
        </p>
      )}

      {data.priceChanges && <PriceChangeNotice report={data.priceChanges} />}

      {cost.unattributedMicroUsd > 0 && (
        <p className="text-xs text-muted-foreground">
          {formatMicroUsd(cost.unattributedMicroUsd)} of this month&apos;s cost
          came from work outside any user&apos;s request, such as scheduled
          jobs. It is in the totals above but not in any user&apos;s figure.
        </p>
      )}

      {/* Tier breakdown */}
      <div className="rounded-xl border border-border/50 bg-card p-5">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
          By Tier ({month})
        </h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {Object.entries(data.tierBreakdown).map(([tier, stats]) => (
            <div key={tier} className="rounded-xl border border-border/50 p-4">
              <TierPill tier={tier} />
              <div className="mt-3 space-y-1 text-sm">
                <p>
                  <span className="font-semibold tabular-nums">
                    {stats.users.toLocaleString()}
                  </span>{" "}
                  <span className="text-muted-foreground">users</span>
                </p>
                <p>
                  <span className="font-semibold tabular-nums">
                    {stats.generations.toLocaleString()}
                  </span>{" "}
                  <span className="text-muted-foreground">
                    generations
                    {stats.credits !== undefined &&
                      ` (${stats.credits.toLocaleString()} credits)`}
                  </span>
                </p>
                <p>
                  <span className="font-semibold tabular-nums">
                    {formatMicroUsd(stats.costMicroUsd)}
                  </span>{" "}
                  <span className="text-muted-foreground">cost</span>
                </p>
                {stats.revenueMicroUsd !== undefined && (
                  <p>
                    <span className="font-semibold tabular-nums">
                      {formatMicroUsd(stats.revenueMicroUsd)}
                    </span>{" "}
                    <span className="text-muted-foreground">
                      revenue
                      {(stats.unpricedUsers ?? 0) > 0 &&
                        ` (${stats.unpricedUsers} unpriced)`}
                    </span>
                  </p>
                )}
                {stats.marginMicroUsd !== undefined && (
                  <p>
                    <MarginFigure
                      microUsd={stats.marginMicroUsd}
                      className="font-semibold tabular-nums"
                    />{" "}
                    <span className="text-muted-foreground">margin</span>
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {data.belowCost && <BelowCostList users={data.belowCost} />}

      {/* Top users */}
      <div className="overflow-hidden rounded-xl border border-border/50 bg-card">
        <div className="flex flex-col gap-3 px-6 pb-3 pt-5 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Top Users ({month})
          </h2>
          <SegmentedControl
            label="Rank top users"
            value={ranking}
            onChange={setRanking}
            options={RANKINGS.map((r) => ({ value: r.key, label: r.label }))}
          />
        </div>
        <TopUsersTable
          users={ranking === "cost" ? data.topCostUsers : data.topUsers}
        />
      </div>
    </div>
  );
}
